import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// UI state that is purely client-side and persists across reloads.
// Server-state (user, entities, employees, attendance, …) lives in
// React Query — not here.

export type ThemeMode = 'light' | 'dark';

export interface UiState {
  // ─── theme ─────────────────────────────────────────────────
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;

  // ─── sidebar ───────────────────────────────────────────────
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // ─── mobile drawer (<768px) ────────────────────────────────
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  closeDrawer: () => void;

  // ─── current entity filter ─────────────────────────────────
  // 'all' or one of the entity IDs from @pointage/shared. Mémorisé
  // par appareil (persist) et reflété dans l'URL ?entity= ; EntitySelect
  // est seul responsable de la synchronisation : l'URL gagne quand elle
  // porte le paramètre, sinon la valeur mémorisée est réécrite dedans.
  currentEntityId: string;
  setCurrentEntityId: (id: string) => void;

  // ─── entity-bar extras (Saisie passes per-entity counts + caption
  // to the shared EntityBar; other screens leave it null → unchanged).
  entityBarExtras: { counts: Record<string, number>; caption: string } | null;
  setEntityBarExtras: (extras: { counts: Record<string, number>; caption: string } | null) => void;

  // ─── entity-bar flash ──────────────────────────────────────
  // Briefly highlights the EntityBar to draw the eye when an
  // entity-scoped action fails because no entity is selected.
  // Toggles true → auto-resets to false after 1.5s. The bar
  // component reads this flag and applies a class.
  entityBarFlashing: boolean;
  flashEntityBar: () => void;

  // ─── changement de société en cours ────────────────────────
  // Ouvert par EntitySelect au clic, refermé par useEntitySwitchLanding
  // (AppShell) quand les requêtes sont retombées. `entityLandedSeq`
  // s'incrémente à chaque atterrissage : la page y accroche son
  // animation de remontée.
  entitySwitch: { targetId: string; since: number } | null;
  entityLandedSeq: number;
  beginEntitySwitch: (targetId: string) => void;
  endEntitySwitch: () => void;
}

// Canonical theme key read by the no-flash <script> in index.html.
// We dual-write here so that script can paint the right theme before
// React mounts. The zustand persist envelope (pointage:ui) also keeps
// `theme` for backward-compat.
const THEME_KEY = 'pointage:theme';

// Push a theme to the DOM + the canonical localStorage key. The
// CSS variables in tokens.css cascade from :root[data-theme=…], so
// setting the attribute is the whole "apply theme" mechanism.
function applyTheme(theme: ThemeMode): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* storage unavailable (private mode / SSR) — non-fatal */
    }
  }
}

// Default theme picker, used only on a fresh load with no stored
// value. The inline script in index.html has already resolved and
// applied the theme to documentElement; read it back so the store
// agrees with what the user actually sees. Falls back to the stored
// key, then OS preference, then light.
function initialTheme(): ThemeMode {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'light' || attr === 'dark') return attr;
  }
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage?.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: initialTheme(),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        const next = get().theme === 'light' ? 'dark' : 'light';
        applyTheme(next);
        set({ theme: next });
      },

      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),

      drawerOpen: false,
      setDrawerOpen: (open) => set({ drawerOpen: open }),
      closeDrawer: () => set({ drawerOpen: false }),

      currentEntityId: 'all',
      setCurrentEntityId: (id) => set({ currentEntityId: id }),

      entityBarExtras: null,
      setEntityBarExtras: (extras) => set({ entityBarExtras: extras }),

      entitySwitch: null,
      entityLandedSeq: 0,
      beginEntitySwitch: (targetId) => set({ entitySwitch: { targetId, since: Date.now() } }),
      endEntitySwitch: () =>
        set((s) => (s.entitySwitch ? { entitySwitch: null, entityLandedSeq: s.entityLandedSeq + 1 } : s)),

      entityBarFlashing: false,
      flashEntityBar: () => {
        set({ entityBarFlashing: true });
        if (typeof window !== 'undefined') {
          window.setTimeout(() => set({ entityBarFlashing: false }), 1500);
        }
      },
    }),
    {
      name: 'pointage:ui',
      // Don't persist drawerOpen — that's transient. currentEntityId is
      // persisted so the société survives a change of page (the sidebar
      // links carry no ?entity=) and a reload.
      partialize: (s) => ({
        theme: s.theme,
        sidebarCollapsed: s.sidebarCollapsed,
        currentEntityId: s.currentEntityId,
      }),
    },
  ),
);
